import { OCCASION_CONFIG } from "@listou/shared/constants";
import type {
  AdminStatsResponse,
  AdminSystemResponse,
  AdminUsageResponse,
} from "@listou/shared/types/api";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";

import {
  amazonEstimate,
  DEFAULT_COMMISSION,
  DEFAULT_CONVERSION,
} from "../components/admin/affiliate";
import { Columns, dayLabel, Heatmap, lastDays, Lines, Split } from "../components/admin/charts";
import { Kpi } from "../components/admin/kpi";
import {
  Box,
  bytes,
  euros,
  Favicon,
  Grid,
  Meter,
  n,
  Pending,
  Row,
  TONES,
  useAdminDays,
  usd,
  type Tone,
} from "../components/admin/retro";
import { isAlarm, PROVIDER_LABELS, usageTotals } from "../components/admin/usage";
import {
  adminAffiliateQuery,
  adminStatsQuery,
  adminSystemQuery,
  adminUsageQuery,
} from "../lib/queries";

export const Route = createFileRoute("/_authed/admin/")({
  component: OverviewPage,
});

type Alert = { tone: Tone; text: string; to?: "/admin/systeme" | "/admin/conso" };

/** Everything that wants a look, worst first. An empty list is the good news. */
const alertsOf = (system: AdminSystemResponse, usage: AdminUsageResponse | undefined) => {
  const alerts: Alert[] = [];
  const { host, scrapingBee, pipeline, features } = system;
  for (const queue of system.queues) {
    if (queue.workers === 0) {
      alerts.push({ tone: "bad", text: `aucun worker sur ${queue.name}`, to: "/admin/systeme" });
    }
  }
  if (!system.redis) alerts.push({ tone: "bad", text: "redis injoignable", to: "/admin/systeme" });
  for (const entry of usage ? usageTotals(usage) : []) {
    if (isAlarm(entry.provider) && entry.calls > 0) {
      alerts.push({
        tone: "bad",
        text: `${n(entry.calls)} ${PROVIDER_LABELS[entry.provider]} sur ${usage?.days} j`,
        to: "/admin/conso",
      });
    }
  }
  for (const queue of system.queues) {
    if (queue.failed > 0) {
      alerts.push({
        tone: "warn",
        text: `${n(queue.failed)} échecs sur ${queue.name} (7 j)`,
        to: "/admin/systeme",
      });
    }
  }
  if (host.diskTotal && host.diskFree !== null && host.diskFree / host.diskTotal < 0.15) {
    alerts.push({ tone: "warn", text: "disque à plus de 85 %", to: "/admin/systeme" });
  }
  if (host.memoryAvailable / host.memoryTotal < 0.1) {
    alerts.push({ tone: "warn", text: "mémoire à plus de 90 %", to: "/admin/systeme" });
  }
  if ((host.loadAverage[1] ?? 0) > host.cpus) {
    alerts.push({ tone: "warn", text: "charge 5 min au-dessus des cpus", to: "/admin/systeme" });
  }
  if (scrapingBee && scrapingBee.usedCredits / Math.max(1, scrapingBee.maxCredits) > 0.8) {
    alerts.push({ tone: "warn", text: "scrapingbee : plus de 80 % des crédits utilisés" });
  }
  const pending = pipeline.wishScrape.find((entry) => entry.status === "pending")?.wishes ?? 0;
  if (pending > 20) {
    alerts.push({ tone: "warn", text: `${n(pending)} envies en attente de scraping` });
  }
  if (!features.amazonTag) alerts.push({ tone: "dim", text: "pas de tag Amazon Partenaires" });
  return alerts;
};

/** One value per day of the period for a usage provider, zero on quiet days. */
const usageDaily = (
  usage: AdminUsageResponse,
  provider: string,
  pick: (entry: AdminUsageResponse["series"][number]) => number,
) =>
  lastDays(usage.days).map((day) =>
    usage.series
      .filter((entry) => entry.day === day && entry.provider === provider)
      .reduce((total, entry) => total + pick(entry), 0),
  );

function OverviewPage() {
  const days = useAdminDays();
  const system = useQuery(adminSystemQuery);
  const usage = useQuery(adminUsageQuery(days));
  const stats = useQuery(adminStatsQuery(days));
  const affiliate = useQuery(adminAffiliateQuery);

  const alerts = system.data ? alertsOf(system.data, usage.data) : [];

  return (
    <div className="grid gap-3">
      {stats.data && usage.data ? (
        <Headlines stats={stats.data} usage={usage.data} />
      ) : (
        <Pending query={stats.data ? usage : stats} />
      )}

      <Grid>
        <Box title="activité" right={`${days} j`} className="md:col-span-2 xl:col-span-3">
          {stats.data ? (
            <Lines
              labels={stats.data.series.map((day) => dayLabel(day.day))}
              height={210}
              series={[
                { label: "listes", values: stats.data.series.map((day) => day.lists) },
                { label: "envies", values: stats.data.series.map((day) => day.wishes) },
                { label: "résas", values: stats.data.series.map((day) => day.reservations) },
                { label: "clics", values: stats.data.series.map((day) => day.clicks) },
              ]}
            />
          ) : (
            <Pending query={stats} />
          )}
        </Box>

        <Box title="alertes" right={system.data ? `${alerts.length}` : undefined}>
          {!system.data && <Pending query={system} />}
          {system.data && alerts.length === 0 && (
            <p style={{ color: "var(--r-green)" }}>■ tout est nominal</p>
          )}
          {alerts.map((alert) => (
            <p key={alert.text} style={{ color: TONES[alert.tone] }}>
              {alert.tone === "bad" ? "✗" : alert.tone === "warn" ? "!" : "·"}{" "}
              {alert.to ? (
                <Link to={alert.to} search={{ j: days }} className="hover:underline">
                  {alert.text}
                </Link>
              ) : (
                alert.text
              )}
            </p>
          ))}
        </Box>

        {stats.data && <Acquisition stats={stats.data} />}
        {usage.data && <Spending usage={usage.data} />}
        {stats.data && <Mix stats={stats.data} />}

        <Box title="amazon" right="estimation">
          {affiliate.data ? (
            <AmazonGlance affiliate={affiliate.data} />
          ) : (
            <Pending query={affiliate} />
          )}
        </Box>

        {system.data && <Health system={system.data} />}
      </Grid>
    </div>
  );
}

/** The first row: what grew, what shrank, at a glance. */
function Headlines({ stats, usage }: { stats: AdminStatsResponse; usage: AdminUsageResponse }) {
  const series = (key: "users" | "lists" | "wishes" | "reservations" | "clicks") =>
    stats.series.map((day) => day[key]);
  const cost = lastDays(usage.days).map(
    (day) =>
      usage.series
        .filter((entry) => entry.day === day)
        .reduce((total, entry) => total + entry.costUsd, 0) +
      // Serper bills in its own credits, a thousandth of a dollar each.
      usage.series
        .filter((entry) => entry.day === day && entry.provider === "serper")
        .reduce((total, entry) => total + entry.units / 1000, 0),
  );
  return (
    <div className="grid gap-1">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-7">
        <Kpi label="inscriptions" values={series("users")} />
        <Kpi label="listes" values={series("lists")} color="var(--r-amber)" />
        <Kpi label="envies" values={series("wishes")} color="var(--r-cyan)" />
        <Kpi label="réservations" values={series("reservations")} />
        <Kpi label="clics" values={series("clicks")} color="var(--r-amber)" />
        <Kpi
          label="scraping (crédits)"
          values={usageDaily(usage, "scrapingbee", (entry) => entry.units)}
          inverse
          color="var(--r-cyan)"
        />
        <Kpi label="coût ia + serper" values={cost} format={usd} inverse color="var(--r-red)" />
      </div>
      <p style={{ color: "var(--r-dim)" }}>
        Totaux sur {stats.days} j. ▲▼ : seconde moitié de la période comparée à la première.
      </p>
    </div>
  );
}

function Acquisition({ stats }: { stats: AdminStatsResponse }) {
  return (
    <>
      <Box title="inscriptions / jour" className="md:col-span-2">
        <Columns
          labels={stats.series.map((day) => dayLabel(day.day))}
          height={120}
          series={[{ label: "comptes", values: stats.series.map((day) => day.users) }]}
        />
        <div className="mt-2">
          <Split
            parts={stats.acquisition.sources.map((source) => ({
              label: source.source ?? "directe",
              values: [source.signups],
            }))}
          />
        </div>
      </Box>
      <Box title="envies ajoutées" right="calendrier" className="md:col-span-2">
        <Heatmap
          days={stats.series.map((day) => ({ day: day.day, value: day.wishes }))}
          label="envies"
        />
      </Box>
    </>
  );
}

function Spending({ usage }: { usage: AdminUsageResponse }) {
  const labels = lastDays(usage.days).map(dayLabel);
  const alarms = usageTotals(usage).filter((entry) => isAlarm(entry.provider) && entry.calls > 0);
  return (
    <Box title="appels payants / jour" className="md:col-span-2">
      <Columns
        labels={labels}
        height={120}
        series={(["openai", "scrapingbee", "serper", "mail"] as const).map((provider) => ({
          label: PROVIDER_LABELS[provider],
          values: usageDaily(usage, provider, (entry) => entry.calls),
        }))}
      />
      {alarms.length > 0 && (
        <p className="mt-1" style={{ color: "var(--r-red)" }}>
          ⚠{" "}
          {alarms
            .map((entry) => `${n(entry.calls)} ${PROVIDER_LABELS[entry.provider]}`)
            .join(" · ")}
        </p>
      )}
    </Box>
  );
}

function Mix({ stats }: { stats: AdminStatsResponse }) {
  return (
    <Box title="répartition" right={`${stats.days} j`} className="md:col-span-2">
      <p style={{ color: "var(--r-dim)" }}>occasions des listes</p>
      <Split
        parts={stats.occasions.map((occasion) => ({
          label: OCCASION_CONFIG[occasion.occasion].label,
          values: [occasion.lists],
        }))}
      />
      <p className="mt-2" style={{ color: "var(--r-dim)" }}>
        clics par boutique
      </p>
      <Split
        parts={stats.clicks.merchants.slice(0, 6).map((merchant) => ({
          label: merchant.merchant,
          icon: <Favicon host={merchant.merchant} />,
          values: [merchant.clicks],
        }))}
      />
    </Box>
  );
}

function AmazonGlance({ affiliate }: { affiliate: Parameters<typeof amazonEstimate>[0] }) {
  const days = useAdminDays();
  const estimate = amazonEstimate(affiliate, DEFAULT_COMMISSION, DEFAULT_CONVERSION);
  return (
    <>
      <Columns
        labels={affiliate.months.map((month) => month.month.slice(5))}
        height={70}
        legend={false}
        series={[
          {
            label: "amazon.fr",
            values: affiliate.months.map((month) => month.amazonFr),
            color: "var(--r-amber)",
          },
        ]}
      />
      <Row label="revenu estimé / mois" tone="ok">
        {euros(estimate.revenueCentsPerMonth)}
      </Row>
      <Row label="ventes en 180 j (min 3)" tone={estimate.salesInWindow >= 3 ? "ok" : "bad"}>
        {estimate.salesInWindow.toFixed(1)}
      </Row>
      <Link
        to="/admin/sites"
        search={{ j: days }}
        className="hover:underline"
        style={{ color: "var(--r-dim)" }}
      >
        → sites et hypothèses
      </Link>
    </>
  );
}

/** The machine in one line each; the detail is on the system tab. */
function Health({ system }: { system: AdminSystemResponse }) {
  const days = useAdminDays();
  const { host } = system;
  const memoryUsed = host.memoryTotal - host.memoryAvailable;
  const workers = system.queues.filter((queue) => queue.workers > 0).length;
  return (
    <Box title="santé" className="md:col-span-2 xl:col-span-3">
      <div className="grid gap-x-6 sm:grid-cols-2">
        <div>
          <Row label="load">
            <Meter ratio={(host.loadAverage[0] ?? 0) / host.cpus} />
          </Row>
          <Row label={`ram ${bytes(memoryUsed)}`}>
            <Meter ratio={memoryUsed / host.memoryTotal} />
          </Row>
          {host.diskTotal !== null && host.diskFree !== null && (
            <Row label="disque">
              <Meter ratio={(host.diskTotal - host.diskFree) / host.diskTotal} />
            </Row>
          )}
        </div>
        <div>
          <Row label="files avec worker" tone={workers === system.queues.length ? "ok" : "bad"}>
            {workers} / {system.queues.length}
          </Row>
          <Row label="scrapingbee restant">
            {system.scrapingBee
              ? n(system.scrapingBee.maxCredits - system.scrapingBee.usedCredits)
              : "-"}
          </Row>
          <Row label="serper restant">
            {system.serperBalance === null ? "-" : n(system.serperBalance)}
          </Row>
          <Link
            to="/admin/systeme"
            search={{ j: days }}
            className="hover:underline"
            style={{ color: "var(--r-dim)" }}
          >
            → détail système
          </Link>
        </div>
      </div>
    </Box>
  );
}
